import React, {PropsWithChildren, ReactNode, RefObject} from "react";
import PortalDocument from "../document/PortalDocument";
import Exception from "../../shared/exception/Exception";
import PortalContext from "../PortalContext";
import {Limits, UserCredentials} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Translate from "../../shared/translation/Translate";
import FunctionHelper from "../../shared/helper/FunctionHelper";
import PortalPureComponent from "../../shared/components/PortalPureComponent";

export enum DropzoneMode {
	UPLOAD = "upload",
	CONVERT = "convert",
	ARCHIVE = "archive",
	OCR = "ocr"
}

export interface DropzoneGlobals {
	dropzoneMode: DropzoneMode
}

export function initDropzoneGlobals(): DropzoneGlobals {
	return {
		dropzoneMode: DropzoneMode.UPLOAD
	};
}

interface DropzoneProperties extends DropzoneGlobals {
	className?: string;
	tooltip?: string;
	context: PortalContext;
	inputRef?: RefObject<HTMLInputElement>;
	onUploadStart?: (files: FileList) => void;
	onError?: (exception: Exception) => void;
}

interface DropzoneState {
	dragover: boolean;
}

export default class Dropzone extends PortalPureComponent<PropsWithChildren<DropzoneProperties>, DropzoneState> {
	constructor(props: Readonly<PropsWithChildren<DropzoneProperties>>) {
		super(props);

		this.state = {
			dragover: false
		};

		this.onUploadStart = this.onUploadStart.bind(this);
	}

	private onUploadStart(filelist: FileList): void {
		let user: UserCredentials | undefined = this.props.context.user;
		let diskSpaceLimit: number = FunctionHelper.applyValue(
			user?.userLimits?.diskSpaceLimit, Limits.getDiskSpaceLimitDefault()
		);
		let uploadLimit: number = FunctionHelper.applyValue(
			user?.userLimits?.uploadLimit, Limits.getUploadLimitDefault()
		);
		let maxFiles: number = FunctionHelper.applyValue(
			user?.userLimits?.maxFiles, Limits.getMaxFilesDefault()
		);
		let portalDocuments: Array<PortalDocument> = this.props.context.documentManager.getDocuments();
		diskSpaceLimit = diskSpaceLimit * 1024 * 1024;
		uploadLimit = uploadLimit * 1024 * 1024;

		let currentWorkspace: number = 0;
		for (let portalDocument of portalDocuments) {
			currentWorkspace += FunctionHelper.applyValue(portalDocument.getDocumentFile()?.fileSize, 0);
		}

		let addedWorkspace: number = 0;
		for (let file of filelist) {
			if (uploadLimit > 0 && file.size > uploadLimit) {
				if (FunctionHelper.exists(this.props.onError)) {
					this.props.onError!(
						new Exception(Translate.value("errors.upload.size", {ns: "message"}))
					);

					return;
				}
			}

			addedWorkspace += file.size;
		}

		if (diskSpaceLimit > 0 && currentWorkspace + addedWorkspace > diskSpaceLimit) {
			if (FunctionHelper.exists(this.props.onError)) {
				this.props.onError!(
					new Exception(Translate.value("errors.upload.workspace", {ns: "message"}))
				);
			}

			return;
		}

		if (maxFiles > 0 && portalDocuments.length + filelist.length > maxFiles) {
			if (FunctionHelper.exists(this.props.onError)) {
				this.props.onError!(
					new Exception(Translate.value("errors.upload.documents", {ns: "message"}))
				);
			}

			return;
		}

		if (FunctionHelper.exists(this.props.onUploadStart)) {
			this.props.onUploadStart!(filelist);
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let className: Array<string> = ["dropzone", "tooltip"];

		if (FunctionHelper.exists(this.props.className)) {
			className = className.concat(this.props.className!.split(" "));
		}

		if (this.state.dragover) {
			className.push("dragover");
		}

		return (
			<div className={className.join(" ")}
				 data-pr-tooltip={this.props.tooltip}
				 onDragEnterCapture={() => this.setState({
					 dragover: true
				 })}
				 onDragLeaveCapture={() => this.setState({
					 dragover: false
				 })}
				 onDropCapture={(event) => {
					 this.setState({
						 dragover: false
					 });

					 this.onUploadStart(event.dataTransfer.files);
				 }}
				 onDragOverCapture={() => this.setState({
					 dragover: true
				 })}>
				{this.props.children}
				<input type="file" multiple ref={this.props.inputRef} title={""}
					   onChange={
						   (event: React.ChangeEvent<HTMLInputElement>) => {
							   if (event.target.files !== null) {
								   this.onUploadStart(event.target.files);
							   }
						   }
					   }/>
			</div>
		);
	}
}